import React, { PureComponent, ReactNode } from 'react';
import { Icon, Input, Layout, Pagination, Table } from 'antd';
import { ColumnProps, PaginationConfig, SorterResult, TableProps, TableRowSelection } from 'antd/lib/table';
import { RangePickerValue } from 'antd/lib/date-picker/interface';
import moment from 'moment';
import { isEqual, isString } from 'lodash';

import {
  IReportConfigProps,
  IReportCustomProps,
  IReportInfoProps,
  IReportQueryProps,
  IReportSortProps,
  IReportViewProps,
} from '@duoke/tools/umd/report';
import { convertCamel, inverseCamel } from '@duoke/tools/umd/utils';
import { createPrefixClassnames } from 'src/tools';
import { beforeDate, formatDate } from 'src/tools/date';
import { DokDatePicker, DokImage, DokSpin, DokTags } from 'src/components';
import { pageConfig } from 'src/config/constants';
import FilterSider, { IReportQueryPropsForMultiplePrecisions } from './Sider';
import TopOperation, { IBatchOperation } from './TopOperation';
import batchLogic, { BatchLogicWrapperProps } from '../BatchLogic';
import styles from './index.less';
import { preTreatReportFetchParamV2 } from './utils';

const { Search } = Input;
const { Content, Footer } = Layout;
const dok = createPrefixClassnames(styles, 'table');

type ITableSourceItem = Record<string, { value: string; name: string }>;

export interface ITableSource<T> {
  data: T[];
  page: Record<string, number>;
  summary: T[];
}

interface IScrollProps {
  x?: boolean | number | string;
  y?: boolean | number | string;
}

const defaultProps = {
  basicConfig: {} as IReportConfigProps, // 基础配置项
  columnConfig: new Map(), // table column 配置
};
const initialState = {
  params: {} as IReportQueryProps,
  sortMap: new Map(), // 排序
  collapsed: false, // 侧边筛选栏是否展开
  scroll: {} as IScrollProps, // Table的scroll
};

interface IProps<T> {
  type: string;
  reqId?: string;
  title?: ReactNode;
  otherOperation?: ReactNode;
  operation?: ColumnProps<T>;
  custom: IReportCustomProps; // 用户自定义配置
  basicConfig: IReportConfigProps; // 侧边栏基础配置
  source: ITableSource<T>; // 报表数据
  loading: boolean; // 全局 Loading
  onChange: (value: IReportQueryProps & IReportQueryPropsForMultiplePrecisions) => void; // 请求数据
  onExport?: (value: IReportQueryProps) => ITableSource<T>; // 导出
  columnConfig?: Map<string, ColumnProps<T>>; // table column 配置
  message: ReactNode; // 批量操作选中提示
  tableOption: TableProps<T>; // 表格其它配置项
  rowSelection?: TableRowSelection<T>;
  selectedCount?: number;
  selectedRecord: { is_all: number; ids: string[] };
  batchOperation?: IBatchOperation;
}

type State = Readonly<typeof initialState>;

const dateTitle = ['precision_day', 'precision_month', 'precision_year'];

class DokTable<T extends ITableSourceItem> extends PureComponent<BatchLogicWrapperProps<IProps<T>>,
  State> {
  updater: any;

  get columns() {
    const { operation, basicConfig, columnConfig } = this.props;
    const {
      scroll,
      sortMap,
      params: { sorts = [], views = [], precision },
    } = this.state;

    const styleKey = basicConfig.info.styleKey;
    const viewMap = (basicConfig.views || []).reduce(
      (acc, v) => ((acc[v.value] = v), acc),
      {} as Record<string, IReportViewProps>,
    );
    let viewsNew = views.filter(v => v.isSelected);

    // todo 判断数据已加载
    if (Number(styleKey) === 11 && this.props?.custom?.precisions?.length === 2) {
      const firstColKey = Number(this.props.custom.precisions[0]);
      viewsNew = viewsNew.filter(v => {
        return v.precisionKeys.find(k => Number(k) === firstColKey);
      });
      if (this.props?.source?.dynamicViews ?? false) {
        viewsNew = [...viewsNew, ...this.props.source.dynamicViews.map((v) => {
          viewMap[v.fieldKey] = {
            name: v.fieldName,
          };
          return {
            fieldKey: v.fieldKey,
            fieldName: v.fieldName,
          };
        })];
      }
    }

    return [
      ...viewsNew
        .map((v) => ({
          width: 120,
          title: dateTitle.includes(v.fieldKey) ? ('日期') : (
            <div className='ellipse'>{(viewMap[v.fieldKey] || {}).name}</div>),
          sorter: sortMap.has(v.fieldKey),
          dataIndex: convertCamel(v.fieldKey),
          sortDirections: ['descend', 'ascend'],
          fixed: scroll.x ? v.fixed || false : false,
          sortOrder:
            v.fieldKey === (sorts[0] || {}).fieldKey
              ? (`${(sorts[0] || {}).order}end` as 'ascend' | 'descend')
              : false,
          render: (t: ITableSourceItem) => {
            const conf = viewMap[v.fieldKey] || {};
            if (styleKey === '1' && Number(conf.areaKey) === 1 && Number(conf.subStyleKey) === 1) {
              const host = (basicConfig.info || {}).imageHost;
              return <DokImage images={t && t.name ? [`${host}${t.name}`] : []} />;
            }
            return (
              <div className={styles['word-break']}>{t && typeof t === 'object' ? t.name : t}</div>
            );
          },
          ...((columnConfig || new Map()).get(v.fieldKey) || {}),
        })),
      { ...operation, fixed: scroll.x ? (operation || {}).fixed || false : false },
    ] as Array<ColumnProps<T>>;
  }

  get tagValue() {
    const {
      basicConfig: { filterGroup = [], preciseSearch, info, filters },
    } = this.props;
    const { params } = this.state;

    const tags = Object.keys(params.filters || {})
      .map((k) => {
        const val = isString(params.filters[k]) ? undefined : (params.filters[k] || {}).val;
        const index = filterGroup.indexOf(k);
        let item = (index === -1 ? preciseSearch.find((v) => v.value === k) : filters[index]) || {
          name: '',
        };

        return {
          key: k,
          index,
          title: item.name || info.timeName,
          value: val && val.length ? val : undefined,
        };
      })
      .filter((v) => v.key && v.value);
    if (tags.length < 2 && !tags.filter((v) => v.index > -1).length) {
      return [];
    }
    return tags;
  }

  static defaultProps = defaultProps;
  readonly state = initialState;
  operation: HTMLDivElement | null = null;

  componentDidMount() {
    this.updateSize();
    window.addEventListener('resize', this.updateSize);
  }

  componentDidUpdate(prevProps: IProps<T>) {
    const { basicConfig } = this.props;
    const { params } = this.state;

    const { sorts: prevSort, ...prevConfig } = prevProps.basicConfig;
    const { sorts, ...config } = basicConfig;
    if (!isEqual(prevConfig, config)) {
      this.handleList(true);
    } else if (basicConfig.info && (!params.views || !params.views.length)) {
      this.handleList();
    }
    setTimeout(this.updateSize, 300);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateSize);
  }

  render() {
    const {
      type,
      reqId,
      title,
      loading,
      onExport,
      tableOption,
      rowSelection,
      otherOperation,
      batchOperation,
      basicConfig,
      basicConfig: { info = {} as IReportInfoProps },
      source,
      selectedCount,
      selectedRecord,
    } = this.props;
    const { page = {}, data } = source || {};
    const { collapsed, scroll, params } = this.state;
    const status = info.reportStatus || {};
    return (
      <DokSpin size='large' spinning={loading}>
        <Layout className={dok('wrapper')}>
          <FilterSider
            values={params}
            config={basicConfig}
            collapsed={collapsed}
            onChange={this.handleChange}
            onCollapse={this.handleCollapse}
          />
          <Layout>
            <Content>
              <div className={dok('content')}>
                <div className={dok('content-top')}>
                  <div className={dok('content-top-title')}>
                    {collapsed && (
                      <Icon
                        type='right-square'
                        onClick={this.handleCollapse}
                        className={dok('filter-collapse-icon')}
                      />
                    )}
                    <div className={dok('title')}>
                      <span>{info.name || title || document.title.replace(' - 多客', '')}</span>
                      {status.refreshStatus === 'NOT_REFRESH' && !!status.lastRefreshTime && (
                        <>
                          <span className={dok('title-refresh')}>
                            最近刷新：{moment(status.lastRefreshTime).format('MM-DD HH:mm')}
                          </span>
                          {reqId && (
                            <span
                              className={dok('title-refresh')}
                              style={{ color: '#BEBEBE', marginLeft: 0 }}
                            >
                              ({reqId})
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <TopOperation
                    type={type}
                    source={source}
                    params={params}
                    getId={this.getId}
                    onExport={onExport}
                    basicConfig={basicConfig}
                    selectedCount={selectedCount}
                    otherOperation={otherOperation}
                    batchOperation={batchOperation}
                    selectedRecord={selectedRecord}
                  />
                </div>
                {this.renderFilter()}
                <div className={styles['table-box']}>
                  <Table
                    size='small'
                    scroll={scroll}
                    dataSource={data}
                    pagination={false}
                    columns={this.columns}
                    className={styles.table}
                    rowSelection={rowSelection}
                    {...tableOption}
                    onChange={this.handleTableChange}
                  />
                </div>
              </div>
              {page && !!page.total && page.total > page.size && (
                <Footer className={dok('footer')}>
                  <Pagination
                    size='small'
                    showSizeChanger
                    total={page.total || 0}
                    pageSize={page.size || 80}
                    current={page.startNo || 1}
                    onChange={this.handlePageChange}
                    className={dok('footer-simple-page')}
                    onShowSizeChange={this.handlePageChange}
                    {...pageConfig}
                  />
                </Footer>
              )}
            </Content>

            {/* {page && !!page.total && (
              <Footer className={dok('footer')}>
                <Pagination
                  size="small"
                  showQuickJumper
                  showSizeChanger
                  total={page.total}
                  pageSize={page.size}
                  current={page.startNo}
                  onChange={this.handlePageChange}
                  onShowSizeChange={this.handlePageChange}
                  showTotal={(total) => `共${total}条`}
                  {...pageConfig}
                />
              </Footer>
            )} */}
          </Layout>
        </Layout>
      </DokSpin>
    );
  }

  getId = (record: any, i: number) => {
    const {
      tableOption: { rowKey = 'key' },
    } = this.props;

    return typeof rowKey === 'function' ? rowKey(record, i) : record[rowKey];
  };
  updateSize = () => {
    const { source } = this.props;
    const { page = {} } = source || {};
    // const { defaultColumn, operation = {} } = this.props;
    const scroll: IScrollProps = {
      y:
        window.innerHeight -
        210 -
        ((this.operation || {}).clientHeight || 55) +
        (page && !!page.total && page.total > page.size ? 0 : 40),
    };
    // const otherX = Number(operation.width || 0);
    // const x = (defaultColumn || []).reduce(
    //   (acc, v) => ((acc += !v.unChecked ? Number(v.width) : 0), acc),
    //   50 + otherX
    // );
    const x = this.columns.reduce((acc, v) => ((acc += Number(v.width || 0)), acc), 0 as number);
    // const x = this.columns.reduce((acc, v) => ((acc += Number(v.width)), acc), 50);
    if (x > ((this.operation || {}).clientWidth || 100) - 80) {
      scroll.x = x;
    }
    const { scroll: prevS } = this.state;

    if (this.updater.isMounted(this) && (prevS.x !== scroll.x || prevS.y !== scroll.y)) {
      this.setState({ scroll });
    }
  };
  renderFilter = () => {
    const {
      message,
      basicConfig: { preciseSearch },
    } = this.props;
    const searchText = preciseSearch
      .filter((v) => v.type === 'text')
      .map((v) => v.name)
      .join();

    return (
      <div ref={(c) => (this.operation = c)} className={dok('operation')}>
        <div className={dok('operation-filter')}>
          {message ? (
            message
          ) : this.tagValue && this.tagValue.length ? (
            <div>
              <span>筛选条件：</span>
              {this.tagValue.map((v, i) => (
                <DokTags
                  inline
                  key={`${v.key}_${i}`}
                  dataSource={this.formatTag(v)}
                  onChange={this.handleTagChange(v.key)}
                />
              ))}
            </div>
          ) : (
            <div>
              {false && searchText && (
                <div className={dok('operation-filter-search')}>
                  <Search
                    allowClear
                    size='small'
                    onSearch={this.handleSearch}
                    placeholder={`搜索${searchText}`}
                  />
                </div>
              )}
              {this.renderDatePicker()}
            </div>
          )}
        </div>
      </div>
    );
  };
  formatTag = (v: any) => {
    const {
      basicConfig: { filters = [] },
    } = this.props;

    const getName = (n: string) => (v.title ? `${v.title}: ${n}` : n);
    if (v.index === -1) {
      return [
        {
          name: getName(Array.isArray(v.value) ? v.value.join('~') : v.value),
          value: v.value,
        },
      ];
    }
    return [
      {
        name: `-${(filters[v.index] || {}).name || ''}为空-`,
        value: -1,
      },
      ...((filters[v.index] || {}).options || []),
    ]
      .filter((o) => (v.value || []).includes(o.value))
      .map((o) => ({ name: getName(o.name), value: o.value }));
  };
  /**
   * 顶部类型(time_selector_top_type)
   * * 0 = 不显示周期选择器
   * * 1 = 有segment(存在多种类型选择器)
   * * 2 = 无segment(只有一种类型选择器)
   * 底部类型(time_selector_bottom_type)
   * * 0 = 不显示周期选择器
   * * 1 = 选天(可以跨) 2019年01月13日-2020年01月13日
   * * 2 = 选天(不可跨) 2020年01月13日
   * * 3 = 选月(可以跨) 2019年01月-2020年01月
   * * 4 = 选月(不可跨) 2020年01月
   * * 5 = 选年(可以跨) 2019年-2020年
   * * 6 = 选年(不可跨) 2020年
   * * 7 = 最近 XX 天、今天、昨天、本周、本月
   */
  renderDatePicker = () => {
    const {
      basicConfig: { info },
    } = this.props;
    if (
      !info.timeSelectorTopType ||
      !info.timeSelectorBottomType ||
      !info.timeSelectorBottomType.filter(Boolean).length
    ) {
      return null;
    }
    return (
      <DokDatePicker
        gutter={1}
        allowClear={false}
        onChange={this.handleDateChange}
        defaultValue={this.getDefaultDate()}
        config={
          info.timeSelectorBottomType.includes(7)
            ? [6, 14, 59, 'today', 'yesterday', 'week', 'month']
            : []
        }
      />
    );
  };
  // 获取日期选择器的默认值
  getDefaultDate = () => {
    const {
      params: { filters },
    } = this.state;

    if (!filters || !filters.sday) {
      return undefined;
    }

    return [
      filters.sday ? moment(filters.sday) : undefined,
      filters.eday ? moment(filters.eday) : undefined,
    ] as RangePickerValue;
  };
  /**
   * 1. 获取默认配置
   * 2. 读取用户自定义配置(每页条数 视图 => 已选中、排序、钉住 精度 排序)
   * 3. 根据默认配置和用户自定义配置生成入参
   * 3. 请求报表数据
   */
  handleList = async (bool?: boolean) => {
    const {
      custom,
      onChange,
      basicConfig,
      basicConfig: { sorts, info },
    } = this.props;
    const { params: prevParams } = this.state;

    const params = preTreatReportFetchParamV2({
      config: basicConfig,
      custom,
    });
    delete params.recentlyData;

    const sortMap = new Map();
    sorts.forEach((v) => {
      sortMap.set(v.value, v);
    });

    if (!isEqual(prevParams, params)) {
      this.setState({ params, sortMap });
    }
    // 筛选项-时间周期处理
    if (params.filters && info.timeFilterKey) {
      params.filters.timeType = params.filters.timeType || (params.filters.sday ? '' : '最近15日');
      params.filters.sday = params.filters.sday || beforeDate();
      params.filters.eday = params.filters.eday || formatDate();
      params.filters[info.timeFilterKey] = {
        val: [params.filters.sday, params.filters.eday],
        action: 'BETWEEN',
      };
    }
    if (bool && onChange) {
      await onChange(params);
    }
  };
  // 更新 筛选项并请求
  handleChange = (origin: IReportQueryProps & IReportQueryPropsForMultiplePrecisions) => {
    const { onChange, basicConfig } = this.props;

    const { filters, sorts } = origin;
    const { _search_filter, ...otherFilter } = filters || {};
    // 更新排序
    const precisions = origin.precisions;
    if (precisions && precisions.length > 0) {
      basicConfig.sorts = basicConfig.views.reduce(
        (acc: IReportSortProps[], v: IReportViewProps) => (
          v.defaultSort &&
          acc.push({
            name: v.name,
            value: v.value,
            isSelected: !acc.length,
            defaultSort: v.defaultSort.toLowerCase(),
          }),
            acc
        ),
        [] as IReportSortProps[],
      );
      const hasSort = basicConfig.sorts.some(
        (v) => sorts && sorts.length && v.value === (sorts || [])[0].fieldKey,
      );
      if (!hasSort) {
        origin.sorts = basicConfig.sorts
          .slice(0, 1)
          .map((v) => ({ fieldKey: v.value, order: v.defaultSort }));
      }
    }

    this.setState({ params: { ...origin } }, async () => {
      if (onChange) {
        await onChange({
          ...origin,
          filters:
            this.tagValue && this.tagValue.length
              ? { ...otherFilter }
              : _search_filter
              ? { _search_filter }
              : filters,
        });
      }
    });
  };
  // 侧边栏展开-收起时的回调函数
  handleCollapse = () => {
    const { collapsed } = this.state;
    this.setState({ collapsed: !collapsed });
  };
  // 关闭标签时，更新筛选项并重新请求
  handleTagChange = (key: string) => {
    return (v: any) => {
      const {
        basicConfig: { info },
      } = this.props;
      const { params } = this.state;

      let convertParams: IReportQueryProps;
      if (!v || !v.length) {
        convertParams = { ...params };
        delete convertParams.filters[key];
        if (info.timeFilterKey === key) {
          convertParams.filters.sday = null;
          convertParams.filters.eday = null;
        }
      } else {
        convertParams = {
          ...params,
          filters: {
            ...params.filters,
            [key]: { ...params.filters[key], val: v },
          },
        };
      }

      this.handleChange({ ...convertParams });
    };
  };
  // 搜索
  handleSearch = (value: string) => {
    const { onChange } = this.props;
    const { params } = this.state;

    const { filters, ...props } = params;

    if (onChange) {
      onChange({
        ...props,
        filters: { _search_filter: value },
      });
    }
  };
  // 统计周期
  handleDateChange = (
    date: RangePickerValue,
    dateString: string[],
    k?: string | number | undefined,
    tip?: string | undefined,
  ) => {
    const {
      basicConfig: { info },
    } = this.props;
    const { params } = this.state;

    const { filters, ...props } = params;

    const type =
      k && tip && tip.startsWith('最近')
        ? `最近${Number(k || 0) + 1}日`
        : tip && /[\u4e00-\u9fa5]/.test(tip)
        ? tip
        : '';
    const cur = {
      ...props,
      filters: {
        ...filters,
        sday: dateString[0],
        eday: dateString[1],
        timeType: type,
        [info.timeFilterKey]: dateString[0]
          ? {
            val: [dateString[0], dateString[1]],
            action: 'BETWEEN',
          }
          : null,
      },
    };

    this.handleChange({ ...cur });
  };
  // 表格 (排序)change
  handleTableChange = (
    pages: PaginationConfig,
    filters: { [name: string]: string[] },
    sorts: SorterResult<T>,
  ) => {
    const { params } = this.state;

    this.handleChange({
      ...params,
      sorts: sorts.order
        ? [
          {
            fieldKey: inverseCamel(sorts.field),
            order: sorts.order.replace('end', ''),
          },
        ]
        : params.sorts && [
        {
          ...params.sorts[0],
          order: params.sorts[0].order === 'asc' ? 'desc' : 'asc',
        },
      ],
    });
  };
  // 页码 change
  handlePageChange = (startNo: number, size?: number) => {
    const { params } = this.state;

    this.handleChange({ ...params, page: { startNo, size: size || (params.page || {}).size } });
  };
}

export default batchLogic<IProps<ITableSourceItem>>(DokTable);
